/**
 * MapView - Delivery routing and dispatch interface
 * Main map container with Google Maps integration
 */
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  getNextNDays, 
  formatDateForTab, 
} from '@/utils/dateUtils';
import { format } from 'date-fns';

// Config and utilities
import { DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from '@/config/constants';
import { loadGoogleMapsLibraries } from '@/utils/googleMapsLoader';
import { dispatchMapId, observeDispatchMarkers } from '@/utils/dispatchMapConfiguration';
import { getOrderNumberSuffix } from '@/utils/markerUtils';
import { getRunCacheLastUpdated, isDesktopOffline } from '@/api/desktopSync';
import { offlineCoordinateForOrder } from '@/utils/offlineMapProjection';
import { classifyManualOrderPlacement } from '@/utils/offlineRunIdentity';

// Hooks
import useRuns from '@/hooks/useRuns';
import useRouting from '@/hooks/useRouting';
import { readOrderCoordinate } from '@/utils/mapCoordinates';
import useOrders from '@/hooks/useOrders';
import useDispatchActions from '@/hooks/useDispatchActions';
import useMarkers from '@/hooks/useMarkers';
import useRunConfirmation from '@/hooks/useRunConfirmation';
import useRunDriverRoster from '@/hooks/useRunDriverRoster';

// Components
import DateRunsPanel from '@/components/DateRunsPanel';
import RunCommandStatus from '@/components/RunCommandStatus';
import RouteStopsPanel from '@/components/RouteStopsPanel';
import OfflineDispatchMapSurface from '@/components/OfflineDispatchMapSurface';
import AssignDriverDialog from '@/components/AssignDriverDialog';
import FlagNoteDialog from '@/components/FlagNoteDialog';
import RepairReservationDialog from '@/components/RepairReservationDialog';
import {
  CreateRunDialog,
  CommitRunDialog,
  UncommitRunDialog,
  RemoveFromCommittedDialog,
  CloseRunDialog
} from '@/components/MapDialogs';

const MapView = ({ driverMode = false }) => {
  const mapRef = useRef(null);
  const prevDateRef = useRef(null);
  const offline = isDesktopOffline();
  const runCacheLastUpdated = offline ? getRunCacheLastUpdated() : null;

  // Map state
  const [map, setMap] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [markersReady, setMarkersReady] = useState(false);
  const [error, setError] = useState(null);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const loadPendingRef = useRef(false);
  const retryRequestedRef = useRef(false);
  const [infoWindow, setInfoWindow] = useState(null);
  
  // Date selection — today + the next two days; older/other dates via the Past
  // calendar. The shop rarely plans runs more than a day or two out.
  const dateTabs = useMemo(() => getNextNDays(3), []);
  const [selectedDate, setSelectedDate] = useState(dateTabs[0]);
  const [isPastDate, setIsPastDate] = useState(false);
  
  const selectedDateString = useMemo(() => {
    if (!selectedDate) return null;
    return format(selectedDate, 'yyyy-MM-dd');
  }, [selectedDate]);

  // Connected Delivery loads a rolling operational window and fetches older
  // dates on demand. Offline Delivery continues reading its complete bounded
  // local cache.
  const { orders, fetchOrders, selectedDateLoaded, acceptDispatchOrder } = useOrders({
    selectedDateString,
    offline,
  });

  const dispatch = useDispatchActions({ orders, fetchOrders, acceptDispatchOrder, selectedDateString, driverMode });
  const flagOpening = useRef(0);

  // Date handlers
  const handleSelectPastDate = (date) => {
    if (date) {
      setSelectedDate(date);
      setIsPastDate(true);
    }
  };

  const handleSelectFutureDate = (date) => {
    setSelectedDate(date);
    setIsPastDate(false);
  };

  // Runs management hook
  const {
    runs,
    runCommands,
    selectedRun,
    isScopeCurrent,
    captureSelection,
    isSelectionCurrent,
    getRun,
    selectRun,
    handleCreateRun,
    handleRenameRun,
    handleDeleteRun,
    handleDiscardCachedRunProposal,
    handleCommitRun,
    handleUncommitRun,
    handleCloseRun,
    handleStartRun,
    handleUnstartRun,
    handleAddToRun,
    handleRemoveFromRun,
    handleDragEnd,
    handleAssignUser,
    handleUnassignUser,
    handleNotifyDriver,
    pendingReservationRepair,
    confirmReservationRepair,
    cancelReservationRepair
  } = useRuns(selectedDateString, { onOrdersChange: fetchOrders, driverMode, offline, runCacheLastUpdated });

  const driverRoster = useRunDriverRoster({ offline, driverMode });
  const driverUsers = driverRoster.drivers;
  const confirmation = useRunConfirmation({ selectedRun, getRun, captureSelection,
    isSelectionCurrent, isScopeCurrent, offline, driverMode, driverRoster });
  const intent = confirmation.intent;
  const openConfirmation = confirmation.open;
  const commitIntent = intent?.action === 'commit' ? intent : null;
  const uncommitIntent = intent?.action === 'uncommit' ? intent : null;
  const closeIntent = intent?.action === 'close' ? intent : null;
  const removeIntent = intent?.action === 'remove' ? intent : null;
  const assignIntent = intent?.action === 'assign' ? intent : null;

  // Get driver color from assigned user
  const driverColor = useMemo(() => {
    if (selectedRun?.user_id && driverUsers.length > 0) {
      const assignedUser = driverUsers.find(u => u.id === selectedRun.user_id);
      return assignedUser?.color || null;
    }
    // Also check populated driver object from backend
    if (selectedRun?.driver?.color) {
      return selectedRun.driver.color;
    }
    return null;
  }, [selectedRun?.user_id, selectedRun?.driver?.color, driverUsers]);

  // Routing hook - pass driver color for polyline
  const {
    routeInfo,
    directionSteps,
    loadingRoute,
    routeError,
    routeWarnings,
    retryRoute,
  } = useRouting(map, driverColor, {
    selectedRun, selectedDateString, driverMode, offline,
    captureSelection, isSelectionCurrent, isScopeCurrent, getRun,
  });
  const [offlineRouteInfo, setOfflineRouteInfo] = useState(null);

  // UI state - Dialogs
  const [showCreateRun, setShowCreateRun] = useState(false);
  const [newRunName, setNewRunName] = useState('');
  const createIntent = useRef(0);
  const changeCreateOpen = (open) => {
    createIntent.current += 1;
    setShowCreateRun(open);
  };
  const changeCreateName = (name) => {
    createIntent.current += 1;
    setNewRunName(name);
  };
  const [flagOrder, setFlagOrder] = useState(null);
  
  // UI state - Panels
  const [showDirections, setShowDirections] = useState(false);
  const [hideCommittedOrders, setHideCommittedOrders] = useState(false);
  const [hoveredStopId, setHoveredStopId] = useState(null);
  const [hoveredAvailableOrderId, setHoveredAvailableOrderId] = useState(null);
  
  // Mobile panel minimize state
  const [leftPanelMinimized, setLeftPanelMinimized] = useState(false);
  const [rightPanelMinimized, setRightPanelMinimized] = useState(false);

  // Filter orders by date
  const filteredOrders = useMemo(() => {
    if (!selectedDateString || !orders?.length) return [];
    return orders.filter(order => order.delivery_date === selectedDateString);
  }, [orders, selectedDateString]);

  // Build stop info map for markers
  const stopInfoMap = useMemo(() => {
    const map = new Map();
    if (!selectedRun?.populated_stops) return map;
    
    selectedRun.populated_stops.forEach((stop) => {
      const order = stop.order;
      if (order) {
        map.set(order.id, {
          sequence: stop.sequence,
          orderSuffix: getOrderNumberSuffix(order.internal_order_number)
        });
      }
    });
    return map;
  }, [selectedRun]);

  // Callback for removing from committed run (triggers dialog)
  const handleRemoveFromCommittedRun = useCallback((orderId, recipientName) => {
    openConfirmation('remove', { orderId, recipientName });
  }, [openConfirmation]);

  // A scope/opening is distinct even after returning to the same order/date.
  const handleEditFlag = useCallback((orderId) => {
    if (!dispatch.canFlag) return;
    const order = orders.find(o => o.id === orderId && o.delivery_date === selectedDateString);
    if (order) setFlagOrder({ order, scope: dispatch.scope, opening: ++flagOpening.current });
  }, [orders, selectedDateString, dispatch.canFlag, dispatch.scope]);
  const handleSaveFlag = (id, note, current) => dispatch.run(id, 'note', note, current);
  const handleOfflineMapPlacement = (id, point, current) => dispatch.run(id, 'place', point, current);
  const handleOfflineMapUnplacement = (id, current) => dispatch.run(id, 'clear', null, current);

  // Markers hook - pass driver color for stop markers
  const { resetInitialFit, updateHoverHighlight, markerError } = useMarkers({
    map: markersReady && !offline ? map : null,
    infoWindow,
    orders: filteredOrders,
    stopInfoMap,
    selectedRun,
    hideCommittedOrders,
    onAddToRun: handleAddToRun,
    onRemoveFromRun: handleRemoveFromRun,
    onRemoveFromCommittedRun: handleRemoveFromCommittedRun,
    onEditFlag: dispatch.canFlag ? handleEditFlag : undefined,
    driverColor
  });

  // Update hover highlighting when hover state changes
  useEffect(() => {
    updateHoverHighlight(hoveredStopId, hoveredAvailableOrderId);
  }, [hoveredStopId, hoveredAvailableOrderId, updateHoverHighlight]);

  // Create run handler
  const onCreateRun = async () => {
    if (runCommands?.blockedCreate) return;
    const intent = ++createIntent.current;
    const success = await handleCreateRun(newRunName);
    if (success === true && isScopeCurrent() && createIntent.current === intent) {
      setShowCreateRun(false);
      setNewRunName('');
    }
  };

  const offlineRunAction = useCallback(() => {
    toast.error('Run changes are unavailable offline');
  }, []);

  const routeHandlers = useMemo(() => ({
    rename: handleRenameRun,
    delete: handleDeleteRun,
    start: offline ? offlineRunAction : handleStartRun,
    unstart: offline ? offlineRunAction : handleUnstartRun,
    addToRun: handleAddToRun,
    removeFromRun: handleRemoveFromRun,
    dragEnd: handleDragEnd,
    notifyDriver: offline ? offlineRunAction : handleNotifyDriver,
  }), [
    offline,
    offlineRunAction,
    handleRenameRun,
    handleDeleteRun,
    handleStartRun,
    handleUnstartRun,
    handleAddToRun,
    handleRemoveFromRun,
    handleDragEnd,
    handleNotifyDriver,
  ]);

  // Loading has page-wide ownership; this effect owns only its UI publication.
  useEffect(() => {
    if (offline) { retryRequestedRef.current = false; return; }
    let current = true;
    loadPendingRef.current = true;
    setError(null);
    const retry = retryRequestedRef.current;
    retryRequestedRef.current = false;
    try { dispatchMapId(); } catch (failure) {
      setError(failure);
      loadPendingRef.current = false;
      return;
    }
    loadGoogleMapsLibraries(['maps', 'core', 'routes', 'marker'], { retry })
      .then(() => { if (current) setIsLoaded(true); })
      .catch(failure => { if (current) setError(failure); })
      .finally(() => { if (current) loadPendingRef.current = false; });
    return () => { current = false; };
  }, [offline, loadAttempt]);

  const retryMapLoading = () => {
    if (offline || loadPendingRef.current || !error?.retryable) return;
    loadPendingRef.current = true;
    retryRequestedRef.current = true;
    setError(null);
    setLoadAttempt(attempt => attempt + 1);
  };

  // Initialize map
  useEffect(() => {
    if (offline) return;
    if (!isLoaded || !mapRef.current || map) return;

    try {
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: DEFAULT_MAP_CENTER,
        zoom: DEFAULT_MAP_ZOOM,
        // Dispatch is a map workspace: wheel zoom must not depend on page scrollability.
        gestureHandling: 'greedy',
        // POI/transit label suppression belongs to this ID's approved cloud style.
        mapId: dispatchMapId(),
        renderingType: window.google.maps.RenderingType.RASTER,
        mapTypeControl: true,
        // Default position is TOP_LEFT, which sits behind the run panel; move it to
        // the open top-center strip so the Map/Satellite buttons are actually visible.
        mapTypeControlOptions: {
          position: window.google.maps.ControlPosition.TOP_CENTER
        },
        streetViewControl: false,
        fullscreenControl: false
      });

      setMap(mapInstance);
      setInfoWindow(new window.google.maps.InfoWindow());
    } catch {
      setError(new Error('Dispatch map could not be initialized. Your orders and run drafts are unchanged.'));
    }
  }, [offline, isLoaded, map]);

  useEffect(() => {
    setMarkersReady(false);
    if (!map || offline) return;
    return observeDispatchMarkers(map, () => setMarkersReady(true), setError);
  }, [map, offline]);

  // Reset state when date changes
  useEffect(() => {
    // Only reset if the date actually changed (not just callback recreation)
    if (prevDateRef.current !== selectedDateString) {
      prevDateRef.current = selectedDateString;
      resetInitialFit();
    }
  }, [selectedDateString, resetInitialFit]);

  // Count orders by date
  const getOrderCountForDate = useCallback((date) => {
    if (!orders?.length) return 0;
    const dateStr = format(date, 'yyyy-MM-dd');
    return orders.filter(order => order.delivery_date === dateStr).length;
  }, [orders]);

  // Tab label helper
  const getTabLabel = useCallback((date, index) => {
    if (index === 0) return 'Today';
    return format(date, 'M/dd');
  }, []);

  // Available orders are designed and ready for run assignment. Manual offline
  // orders also require placement so every run stop has a route coordinate.
  const availableOrders = useMemo(() => {
    let available = filteredOrders.filter(
      (order) => classifyManualOrderPlacement(order).isDispatchReady,
    );
    
    // Exclude orders already in the selected run
    if (selectedRun) {
      const runOrderIds = new Set(selectedRun.populated_stops?.map(s => s.order_id) || []);
      available = available.filter(order => !runOrderIds.has(order.id));
    }
    
    return available;
  }, [filteredOrders, selectedRun]);

  // Accepted orders normally appear as awaiting-design map markers. Manual
  // offline orders stay explicit through design until their second dispatch
  // gate, map placement, is also satisfied.
  const ordersNotShownOnMap = useMemo(() => (
    filteredOrders.filter((order) => {
      if (classifyManualOrderPlacement(order).needsPlacement) {
        return true;
      }
      if (order.status !== 'accepted') return false;
      const coordinate = offline
        ? offlineCoordinateForOrder(order)
        : readOrderCoordinate(order);
      return !coordinate;
    })
  ), [filteredOrders, offline]);

  return (
    <div className="h-full relative bg-stone-100">
      {dispatch.pending.map(item => (
        <div key={item.id} role="status" className="relative z-50 rounded border bg-white p-2 text-sm" data-testid="dispatch-action-status">
          <p>Order {item.id} ({item.date}): {item.state === 'pending' ? item.stalled ? 'Still awaiting the save response. Do not repeat it.' : 'Saving…'
            : item.state === 'accepted' ? `${item.message} Refresh needed.` : item.message}</p>
          {item.state === 'accepted' && <button type="button" onClick={() => dispatch.refresh(item.date)}>Refresh orders</button>}
          {item.state === 'unknown' && <button type="button" onClick={() => dispatch.review(item.id)}>Check current order</button>}
          {item.review && <p>{item.review}</p>}
          {item.canAcknowledge && <button type="button" onClick={() => dispatch.acknowledge(item.id)}>Acknowledge and refresh</button>}
        </div>
      ))}
      {/* Full-screen Map */}
      <div className="absolute inset-0">
        {offline ? (
          <OfflineDispatchMapSurface
            orders={filteredOrders}
            selectedRun={selectedRun}
            driverColor={driverColor}
            selectedDateLabel={selectedDate ? formatDateForTab(selectedDate) : undefined}
            actionScope={dispatch.scope}
            actionBlocked={dispatch.blocked}
            onPlaceOrder={dispatch.canPlace ? handleOfflineMapPlacement : undefined}
            onUnplaceOrder={dispatch.canPlace ? handleOfflineMapUnplacement : undefined}
            onRouteInfoChange={setOfflineRouteInfo}
          />
        ) : (
          <>
        {(error || markerError) && (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-50 bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-xl text-sm">
            <span role="status">{markerError || error?.message || 'Google Maps is unavailable.'}</span>
            {error?.retryable && (
              <button type="button" className="ml-3 underline" onClick={retryMapLoading} data-testid="google-map-retry">Try again</button>
            )}
          </div>
        )}
        
        <div ref={mapRef} className="w-full h-full" data-testid="google-map" />
        
        {(!isLoaded || !markersReady) && !error && !markerError && (
          <div className="absolute inset-0 flex items-center justify-center bg-stone-100">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
              <p className="text-stone-600">Loading Google Maps...</p>
            </div>
          </div>
        )}

        {markersReady && !error && !markerError && selectedDateLoaded && filteredOrders.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-white/90 backdrop-blur-sm px-6 py-4 rounded-xl shadow-soft-lg text-center">
              <p className="text-stone-700 font-medium">
                No orders {selectedDate ? `for ${formatDateForTab(selectedDate)}` : 'to display'}
              </p>
              <p className="text-sm text-stone-500 mt-1">Try selecting a different date</p>
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Left Panel - Date tabs + Runs */}
      <DateRunsPanel
        dateTabs={dateTabs}
        selectedDate={selectedDate}
        isPastDate={isPastDate}
        onSelectFutureDate={handleSelectFutureDate}
        onSelectPastDate={handleSelectPastDate}
        getOrderCountForDate={getOrderCountForDate}
        getTabLabel={getTabLabel}
        filteredOrdersCount={filteredOrders.length}
        runs={runs}
        selectedRunId={selectedRun?.id}
        onSelectRun={selectRun}
        onDeleteRun={routeHandlers.delete}
        onCreateRunClick={() => changeCreateOpen(true)}
        runCommands={runCommands}
        commandStatus={!driverMode && <RunCommandStatus key={runCommands?.scopeKey} commands={runCommands} />}
        offline={offline}
        isMinimized={leftPanelMinimized}
        onToggleMinimize={() => setLeftPanelMinimized(!leftPanelMinimized)}
        driverMode={driverMode}
      />

      {/* Right Panels - Stops + Available Orders */}
      <RouteStopsPanel
        runCommands={runCommands}
        captureSelection={captureSelection}
        isSelectionCurrent={isSelectionCurrent}
        selectedRun={selectedRun}
        runCacheLastUpdated={runCacheLastUpdated}
        routeInfo={offline ? offlineRouteInfo : routeInfo}
        directionSteps={offline ? [] : directionSteps}
        loadingRoute={!offline && loadingRoute}
        routeError={offline ? null : routeError}
        routeWarnings={offline ? [] : routeWarnings}
        onRetryRoute={retryRoute}
        showDirections={showDirections}
        setShowDirections={setShowDirections}
        hideCommittedOrders={hideCommittedOrders}
        setHideCommittedOrders={setHideCommittedOrders}
        hoveredStopId={hoveredStopId}
        setHoveredStopId={setHoveredStopId}
        hoveredAvailableOrderId={hoveredAvailableOrderId}
        setHoveredAvailableOrderId={setHoveredAvailableOrderId}
        onCommit={() => confirmation.open('commit')}
        onUncommit={() => confirmation.open('uncommit')}
        onCloseRun={() => confirmation.open('close')}
        onStartRun={() => routeHandlers.start(selectedRun?.id)}
        onUnstartRun={() => routeHandlers.unstart(selectedRun?.id)}
        onRenameRun={routeHandlers.rename}
        onDiscardOfflineProposal={handleDiscardCachedRunProposal}
        onAddToRun={routeHandlers.addToRun}
        onRemoveFromRun={routeHandlers.removeFromRun}
        onDragEnd={routeHandlers.dragEnd}
        availableOrders={availableOrders}
        ordersNotShownOnMap={ordersNotShownOnMap}
        filteredOrdersCount={filteredOrders.length}
        ordersLoaded={selectedDateLoaded}
        onAssignDriverClick={() => confirmation.open('assign')}
        onNotifyDriver={() => routeHandlers.notifyDriver(selectedRun?.id)}
        isMinimized={rightPanelMinimized}
        onToggleMinimize={() => setRightPanelMinimized(!rightPanelMinimized)}
        driverMode={driverMode}
        offline={offline}
      />

      {/* Dialogs - hidden in driver mode */}
      {!driverMode && (
        <>
          <CreateRunDialog
            open={showCreateRun}
            onOpenChange={changeCreateOpen}
            runName={newRunName}
            onRunNameChange={changeCreateName}
            onSubmit={onCreateRun}
            pending={runCommands?.blockedCreate}
            dateLabel={formatDateForTab(selectedDate)}
          />

          <CommitRunDialog
            pending={runCommands?.blockedRun(commitIntent?.runId)}
            open={!!commitIntent}
            onOpenChange={(open) => { if (!open) confirmation.cancel(commitIntent); }}
            onConfirm={() => {
              if (runCommands?.blockedRun(commitIntent?.runId)) return;
              const target = confirmation.consume(commitIntent);
              if (target) handleCommitRun(target.runId);
            }}
            runName={commitIntent?.runName}
            stopCount={commitIntent?.stopCount || 0}
          />

          <UncommitRunDialog
            pending={runCommands?.blockedRun(uncommitIntent?.runId)}
            open={!!uncommitIntent}
            onOpenChange={(open) => { if (!open) confirmation.cancel(uncommitIntent); }}
            onConfirm={() => {
              if (runCommands?.blockedRun(uncommitIntent?.runId)) return;
              const target = confirmation.consume(uncommitIntent);
              if (target) handleUncommitRun(target.runId);
            }}
            runName={uncommitIntent?.runName}
            stopCount={uncommitIntent?.stopCount || 0}
          />

          <CloseRunDialog
            pending={runCommands?.blockedRun(closeIntent?.runId)}
            open={!!closeIntent}
            onOpenChange={(open) => { if (!open) confirmation.cancel(closeIntent); }}
            onConfirm={() => {
              if (runCommands?.blockedRun(closeIntent?.runId)) return;
              const target = confirmation.consume(closeIntent);
              if (target) handleCloseRun(target.runId);
            }}
            runName={closeIntent?.runName}
            stopCount={closeIntent?.stopCount || 0}
          />

          <RemoveFromCommittedDialog
            pending={runCommands?.blockedRun(removeIntent?.runId)}
            open={!!removeIntent}
            onOpenChange={(open) => { if (!open) confirmation.cancel(removeIntent); }}
            onConfirm={() => {
              if (runCommands?.blockedRun(removeIntent?.runId)) return;
              const target = confirmation.consume(removeIntent);
              if (target) handleRemoveFromRun(target.orderId);
            }}
            recipientName={removeIntent?.recipientName}
            orderNumber={removeIntent?.orderNumber}
            runName={removeIntent?.runName}
          />

          <AssignDriverDialog
            pending={runCommands?.blockedRun(assignIntent?.runId)}
            open={!!assignIntent}
            onOpenChange={(open) => { if (!open) confirmation.cancel(assignIntent); }}
            driverUsers={driverUsers}
            status={driverRoster.status}
            error={driverRoster.error}
            closeOnSubmit={false}
            currentUserId={assignIntent?.currentUserId}
            onAssignUser={(userId) => {
              if (runCommands?.blockedRun(assignIntent?.runId)) return;
              const target = confirmation.consume(assignIntent, { userId });
              if (target) handleAssignUser(userId);
            }}
            onUnassignUser={() => {
              if (runCommands?.blockedRun(assignIntent?.runId)) return;
              const target = confirmation.consume(assignIntent, { unassign: true });
              if (target) handleUnassignUser();
            }}
            runName={assignIntent?.runName}
            offlineProposal={offline}
          />

          <FlagNoteDialog
            key={flagOrder?.opening || 0}
            order={orders.find(item => item.id === flagOrder?.order.id) || flagOrder?.order}
            scope={dispatch.scope}
            open={!!flagOrder && flagOrder.scope === dispatch.scope && dispatch.canFlag}
            blocked={dispatch.blocked(flagOrder?.order.id)}
            onOpenChange={(open) => { if (!open) setFlagOrder(null); }}
            onSave={handleSaveFlag}
            recovery={dispatch.pending.find(item => item.id === flagOrder?.order.id)}
            onCheckCurrent={() => dispatch.review(flagOrder?.order.id)}
            onAcknowledge={() => dispatch.acknowledge(flagOrder?.order.id)}
            onRefresh={() => dispatch.refresh(flagOrder?.order.delivery_date)}
          />

          <RepairReservationDialog
            pending={pendingReservationRepair}
            commandPending={Boolean(runCommands?.blockedRun(pendingReservationRepair?.runId)
              || runCommands?.blockedOrder(pendingReservationRepair?.orderId))}
            onConfirm={confirmReservationRepair}
            onCancel={cancelReservationRepair}
          />
        </>
      )}
    </div>
  );
};

export default MapView;
